/**
 * Load chapter-title Assets into the PdfBook model before native PDF export.
 */
import { isJust, just, nothing, type Maybe } from "../lib/maybe";
import { readProjectAssetDataUrl } from "../lib/tauri/assets";
import { decodeDataUrl } from "./exportAssets";
import type { PdfBook, PdfBookImage, PdfBookLeaf } from "./pdfBook";

async function loadHeadingImage(
  rootPath: string,
  relativePath: string,
  cache: Map<string, PdfBookImage>,
): Promise<Maybe<PdfBookImage>> {
  const cached = cache.get(relativePath);
  if (typeof cached !== "undefined") return just(cached);
  try {
    const dataUrl = await readProjectAssetDataUrl(rootPath, relativePath);
    const decodedM = decodeDataUrl(dataUrl);
    if (isJust(decodedM) === false) return nothing;
    const { mediaType, bytes } = decodedM.value;
    let widthPx = 400;
    let heightPx = 200;
    try {
      const blob = new Blob([bytes as BlobPart], { type: mediaType });
      const bitmap = await createImageBitmap(blob);
      widthPx = Math.max(1, bitmap.width);
      heightPx = Math.max(1, bitmap.height);
      bitmap.close();
    } catch {
      /* keep defaults */
    }
    const binary = Array.from(bytes, (b) => String.fromCharCode(b)).join("");
    const image: PdfBookImage = {
      mime: mediaType,
      bytesBase64: btoa(binary),
      widthPx,
      heightPx,
    };
    cache.set(relativePath, image);
    return just(image);
  } catch {
    return nothing;
  }
}

/** Attach raster bytes for each chapter heading `imagePath`. Maps are unchanged. */
export async function enrichPdfBookHeadingImages(
  book: PdfBook,
  rootPath: string,
): Promise<PdfBook> {
  const cache = new Map<string, PdfBookImage>();
  const body: PdfBookLeaf[] = [];
  for (const leaf of book.body) {
    if (leaf.type === "map") {
      body.push(leaf);
      continue;
    }
    const path = leaf.heading.imagePath;
    let image = leaf.heading.image;
    if (path !== "" && isJust(image) === false) {
      image = await loadHeadingImage(rootPath, path, cache);
    }
    body.push({
      ...leaf,
      heading: { ...leaf.heading, image },
    });
  }
  return { ...book, body };
}
